/**
 * FireGuard Live - Demo API Service
 * Използва се САМО когато REACT_APP_DEMO_MODE=true. Връща данни от demoData.js.
 * Не засяга production – production не задава тази променлива.
 */
import {
  demoDevices,
  demoSprinklerZones,
  demoElectricalPanels,
  demoEvacDoors,
  demoTasks,
  demoInspections,
  demoUsers,
  getDemoDashboard
} from './demoData';

const delay = (ms = 150) => new Promise(resolve => setTimeout(resolve, ms));

const wrap = (data) => Promise.resolve({ data });

const demoAuth = {
  login: async (username, password) => {
    await delay(200);
    const user = demoUsers.find(u =>
      (u.email && u.email.toLowerCase() === (username || '').toLowerCase()) ||
      (u.full_name && u.full_name.toLowerCase().includes((username || '').toLowerCase()))
    ) || (username === 'demo' || username === 'demo@fireguard.bg' ? demoUsers[0] : null);
    if (!user || password !== 'demo123') {
      const err = new Error('Invalid credentials');
      err.response = { status: 401, data: { message: 'Invalid username or password.' } };
      throw err;
    }
    return wrap({
      token: 'demo-jwt-' + Date.now(),
      user: { id: user.id, username: user.email || 'demo', email: user.email, full_name: user.full_name, role: user.role }
    });
  },
  register: async () => { await delay(300); return wrap({ message: 'Demo mode – registration is disabled.' }); },
  logout: async () => { await delay(100); return wrap({}); },
  refreshToken: async () => { await delay(100); return wrap({ token: 'demo-jwt-refresh', user: demoUsers[0] }); },
  getCurrentUser: async () => { await delay(100); return wrap(demoUsers[0]); },
  updateProfile: async () => { await delay(200); return wrap({}); },
  changePassword: async () => { await delay(200); return wrap({}); },
  getSessions: async () => { await delay(100); return wrap([]); },
  terminateSession: async () => { await delay(100); return wrap({}); },
};

const demoDevicesAPI = {
  getAll: async () => {
    await delay(200);
    return wrap({ success: true, devices: demoDevices, data: demoDevices });
  },
  getOne: async (id) => {
    await delay(100);
    const d = demoDevices.find(x => x.id === parseInt(id, 10));
    if (!d) throw Object.assign(new Error('Device not found'), { response: { status: 404 } });
    return wrap(d);
  },
  create: async (data) => { await delay(300); return wrap({ ...data, id: Math.max(...demoDevices.map(x => x.id)) + 1 }); },
  update: async () => { await delay(200); return wrap({}); },
  delete: async () => { await delay(200); return wrap({ success: true }); },
  scanQR: async () => { await delay(200); return wrap({}); },
  regenerateQR: async () => { await delay(200); return wrap({}); },
  getHistory: async () => { await delay(100); return wrap([]); },
  getStatistics: async () => { await delay(100); return wrap({ total: demoDevices.length, online: demoDevices.filter(d => d.status === 'online').length }); },
};

const demoMonitoringAPI = {
  getDashboard: async () => {
    await delay(150);
    const result = getDemoDashboard();
    if (typeof console !== 'undefined' && console.log) {
      console.log('[Demo] Dashboard data loaded', {
        deviceStats: result.dashboard?.deviceStats,
        totalDevices: result.dashboard?.deviceStats && Object.values(result.dashboard.deviceStats).reduce((s, n) => s + n, 0)
      });
    }
    return wrap(result);
  },
  getAlerts: async () => { await delay(100); return wrap([]); },
  resolveAlert: async () => { await delay(100); return wrap({}); },
  createAlert: async () => { await delay(100); return wrap({}); },
  getDeviceStatus: async () => { await delay(100); return wrap({}); },
  getStatistics: async () => { await delay(100); return wrap({}); },
  getHealthCheck: async () => { await delay(100); return wrap({ ok: true }); },
};

const demoMaintenanceAPI = {
  getAll: async () => { await delay(200); return wrap(demoTasks); },
  getTasks: async () => { await delay(200); return wrap(demoTasks); },
  getOne: async (id) => {
    const t = demoTasks.find(x => x.id === parseInt(id, 10));
    if (!t) throw Object.assign(new Error('Task not found'), { response: { status: 404 } });
    return wrap(t);
  },
  create: async () => { await delay(300); return wrap({}); },
  createTask: async () => { await delay(300); return wrap({}); },
  update: async () => { await delay(200); return wrap({}); },
  updateTask: async () => { await delay(200); return wrap({}); },
  delete: async () => { await delay(200); return wrap({}); },
  getMyTasks: async () => { await delay(200); return wrap(demoTasks); },
  getOverdue: async () => { await delay(200); return wrap(demoTasks.filter(t => new Date(t.due_date) < new Date())); },
  assign: async () => { await delay(200); return wrap({}); },
  getStatistics: async () => { await delay(100); return wrap({ total: demoTasks.length, pending: demoTasks.filter(t => t.status === 'pending').length }); },
};

const demoInspectionsAPI = {
  getAll: async () => { await delay(200); return wrap(demoInspections); },
  getOne: async (id) => {
    const i = demoInspections.find(x => x.id === parseInt(id, 10));
    if (!i) throw Object.assign(new Error('Inspection not found'), { response: { status: 404 } });
    return wrap(i);
  },
  create: async () => { await delay(300); return wrap({}); },
  update: async () => { await delay(200); return wrap({}); },
  getByDevice: async () => { await delay(100); return wrap(demoInspections); },
  getMyInspections: async () => { await delay(200); return wrap(demoInspections); },
  getOverdue: async () => { await delay(200); return wrap([]); },
  scheduleFollowup: async () => { await delay(200); return wrap({}); },
  getStatistics: async () => { await delay(100); return wrap({ total: demoInspections.length }); },
};

const demoSprinklerMonitorAPI = {
  getZones: async () => { await delay(200); return wrap({ success: true, zones: demoSprinklerZones }); },
  getZone: async (id) => {
    const z = demoSprinklerZones.find(x => x.id === parseInt(id, 10));
    if (!z) throw Object.assign(new Error('Zone not found'), { response: { status: 404 } });
    return wrap({ success: true, zone: z });
  },
  createZone: async () => { await delay(300); return wrap({ success: true, zone: {} }); },
  updateZone: async () => { await delay(200); return wrap({ success: true, zone: {} }); },
  sendSignals: async () => { await delay(200); return wrap({ success: true }); },
  getZoneEvents: async () => { await delay(100); return wrap([]); },
};

const demoElectricalPanelMonitorAPI = {
  getPanels: async () => { await delay(200); return wrap({ success: true, panels: demoElectricalPanels }); },
  getPanel: async (id) => {
    const p = demoElectricalPanels.find(x => x.id === parseInt(id, 10));
    if (!p) throw Object.assign(new Error('Panel not found'), { response: { status: 404 } });
    return wrap({ success: true, panel: p });
  },
  createPanel: async () => { await delay(300); return wrap({ success: true, panel: {} }); },
  updatePanel: async () => { await delay(200); return wrap({ success: true, panel: {} }); },
  sendSignals: async () => { await delay(200); return wrap({ success: true }); },
  getPanelEvents: async () => { await delay(100); return wrap([]); },
};

const demoEvacDoorMonitorAPI = {
  getDoors: async () => { await delay(200); return wrap({ success: true, doors: demoEvacDoors }); },
  getDoor: async (id) => {
    const d = demoEvacDoors.find(x => x.id === parseInt(id, 10));
    if (!d) throw Object.assign(new Error('Door not found'), { response: { status: 404 } });
    return wrap({ success: true, door: d });
  },
  createDoor: async () => { await delay(300); return wrap({ success: true, door: {} }); },
  updateDoor: async () => { await delay(200); return wrap({ success: true, door: {} }); },
  sendSignals: async () => { await delay(200); return wrap({ success: true }); },
  getDoorEvents: async () => { await delay(100); return wrap([]); },
};

export const demoApiService = {
  auth: demoAuth,
  devices: demoDevicesAPI,
  monitoring: demoMonitoringAPI,
  maintenance: demoMaintenanceAPI,
  inspections: demoInspectionsAPI,
  sprinklerMonitor: demoSprinklerMonitorAPI,
  electricalPanelMonitor: demoElectricalPanelMonitorAPI,
  evacDoorMonitor: demoEvacDoorMonitorAPI,
  ai: {},
};
